import axiosInstance from "./baseUrl";
import {
  CouponListMeta,
  ListCouponsQuery,
} from "../redux/features/types/couponListTypes";

// ---------------------------------------------------------------------------
// Issue cashback — POST /v1/coupon/admin/cashback/{buyerId}
//
// Backend now requires both `percentage` and the source `orderId`. The cashback
// amount = orderAmount * percentage / 100, calculated and recorded by backend.
// ---------------------------------------------------------------------------

export interface IssueCashbackPayload {
  percentage: number;
  orderId: string;
}

export interface IssueCashbackBuyer {
  id: string;
  fullName: string;
  email: string;
}

export interface IssueCashbackIssuer {
  id: string;
  fullName: string;
}

export interface IssueCashbackData {
  couponCode: string;
  cashbackAmount: number;
  cashbackPercentage: number;
  sourceOrderId: string;
  sourceOrderAmount: number;
  buyer: IssueCashbackBuyer;
  issuedBy: IssueCashbackIssuer;
  creditedAt: string;
  newWalletBalance: number;
}

export interface IssueCashbackResponse {
  isSuccessful: boolean;
  message: string;
  data: IssueCashbackData;
}

export const issueCashback = async (
  buyerId: string,
  payload: IssueCashbackPayload
): Promise<IssueCashbackResponse> => {
  const response = await axiosInstance.post(
    `/v1/coupon/admin/cashback/${buyerId}`,
    payload
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// List issued cashbacks — GET /v1/coupon/admin/cashback
//
// Same filter set as /list_coupons and /used_coupons — backend appears to have
// copy-pasted the query shape. Several filters (couponType, status, discountType,
// expiryFrom/To) are nonsensical for cashback issuances, so the cashback tab
// only surfaces the relevant subset in the UI.
// ---------------------------------------------------------------------------

export interface CashbackIssuanceBuyer {
  id: string;
  fullName: string;
  email: string;
}

export interface CashbackIssuanceIssuer {
  id: string;
  fullName: string;
}

export interface CashbackIssuance {
  id: string;
  couponCode: string;
  cashbackAmount: number;
  /** ISO datetime when the cashback was issued. May be null for legacy rows. */
  dateIssued: string | null;
  buyer: CashbackIssuanceBuyer;
  issuedBy: CashbackIssuanceIssuer;
}

export interface CashbackIssuanceListResponse {
  isSuccessful: boolean;
  message: string;
  data: {
    items: CashbackIssuance[];
    meta: CouponListMeta;
  };
}

export const getCashbackIssuances = async (
  params: ListCouponsQuery = {}
): Promise<CashbackIssuanceListResponse> => {
  const response = await axiosInstance.get("/v1/coupon/admin/cashback", {
    params,
  });
  return response.data;
};
